'use client'

import { useState } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { ChevronRight } from 'lucide-react'

import {
    NavigationItem,
    NavigationLink,
    NavigationSection,
} from '../navigation/types'
import {
    isNavigationLinkActive,
    isNavigationSectionActive,
} from '../navigation/navActiveState'
import { useAnalytics } from '@/hooks/useAnalytics'

import { SIDEBAR_SECTION_LABEL_CLASS } from '@/components/ui/sidebarExpandState'
import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from '@/components/ui/sidebar'
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible'

import {
    NAV_LUCIDE_SIZE_CLASS,
    NAV_LUCIDE_STROKE,
} from '../navigation/navIconConstants'
import { SidebarNavRow } from './SidebarNavRow'
import { cn } from '@/lib/utils'

const isNavigationSection = (
    item: NavigationItem,
): item is NavigationSection => {
    return 'items' in item && 'name' in item
}

function NavSidebarLink({
    item,
    currentPath,
    sectionName,
    sectionItems,
}: {
    item: NavigationLink
    currentPath: string
    sectionName?: string
    sectionItems: NavigationLink[]
}) {
    const { trackNavItemClick } = useAnalytics()
    const { reserveExpandedLayout } = useSidebar()
    const isActive = isNavigationLinkActive(item, currentPath, sectionItems)
    const LucideIconComponent = item.lucideIcon ?? null

    const handleClick = (e: React.MouseEvent) => {
        trackNavItemClick(sectionName || 'main_nav', item.label || item.value)

        if (item.openInNewTab && item.href) {
            e.preventDefault()
            window.open(item.href, '_blank')
            return
        }

        reserveExpandedLayout()
    }

    return (
        <SidebarMenuItem>
            <SidebarMenuButton
                asChild
                variant="nav"
                size="nav"
                isActive={isActive}
                className="truncate"
            >
                <Link
                    href={item.href}
                    passHref
                    onClick={handleClick}
                    className="flex w-full min-w-0 items-center"
                >
                    <SidebarNavRow
                        icon={
                            LucideIconComponent ? (
                                <LucideIconComponent
                                    className={NAV_LUCIDE_SIZE_CLASS}
                                    strokeWidth={NAV_LUCIDE_STROKE}
                                    aria-hidden
                                />
                            ) : null
                        }
                    >
                        {item.label}
                    </SidebarNavRow>
                </Link>
            </SidebarMenuButton>
        </SidebarMenuItem>
    )
}

function NavCollapsibleSection({
    section,
    currentPath,
}: {
    section: NavigationSection
    currentPath: string
}) {
    const enabledItems = section.items.filter((item) => item.enabled)
    const isActive = isNavigationSectionActive(section, currentPath)
    const defaultOpen = section.defaultOpen ?? true
    const [manualOpen, setManualOpen] = useState<boolean | null>(null)
    const open = isActive ? true : (manualOpen ?? defaultOpen)
    const sectionHeaderClassName = SIDEBAR_SECTION_LABEL_CLASS
    const collapsibleSectionHeaderClassName = cn(
        sectionHeaderClassName,
        'cursor-pointer transition-colors hover:text-sidebar-foreground',
    )

    return (
        <Collapsible
            open={open}
            onOpenChange={setManualOpen}
            className="group/collapsible"
        >
            <SidebarGroup className="gap-0 px-0 py-1.5">
                {isActive ? (
                    <SidebarGroupLabel className={sectionHeaderClassName}>
                        {section.name}
                    </SidebarGroupLabel>
                ) : (
                    <CollapsibleTrigger asChild>
                        <SidebarGroupLabel
                            asChild
                            className={collapsibleSectionHeaderClassName}
                        >
                            <button
                                type="button"
                                className="flex w-full items-center"
                            >
                                <span>{section.name}</span>
                                <ChevronRight
                                    className="size-3.5 shrink-0 transition-transform duration-300 ease-out group-data-[state=open]/collapsible:rotate-90"
                                    strokeWidth={NAV_LUCIDE_STROKE}
                                    aria-hidden
                                />
                            </button>
                        </SidebarGroupLabel>
                    </CollapsibleTrigger>
                )}
                <CollapsibleContent
                    forceMount
                    className={cn(
                        'grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out',
                        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                    )}
                >
                    <div
                        className={cn(
                            'min-h-0 overflow-hidden',
                            !open && 'pointer-events-none',
                        )}
                        aria-hidden={!open}
                    >
                        <SidebarGroupContent>
                            <SidebarMenu className="gap-0">
                                {enabledItems.map((item) => (
                                    <NavSidebarLink
                                        key={item.value}
                                        item={item}
                                        currentPath={currentPath}
                                        sectionName={section.name}
                                        sectionItems={enabledItems}
                                    />
                                ))}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </div>
                </CollapsibleContent>
            </SidebarGroup>
        </Collapsible>
    )
}

export function NavMain({ items }: { items: NavigationItem[] }) {
    const { pathname: currentPath } = useRouter()
    const sectionItems = items.filter(isNavigationSection)

    return (
        <>
            {sectionItems.map((section) => (
                <NavCollapsibleSection
                    key={section.value}
                    section={section}
                    currentPath={currentPath}
                />
            ))}
        </>
    )
}
